/**
 * Built-in `fs` policy domain — the Playground virtual filesystem.
 *
 * Refs are workspace-relative file paths ("Recipes/soup.md"; a leading slash
 * is tolerated as workspace-anchored and normalized away). Patterns are an
 * exact path, a subtree marked by a trailing slash ("designs/"), or `"/"` for
 * the whole workspace — subtree patterns ARE directory inheritance (a grant on
 * a dir covers the dir and everything under it). The whole workspace is
 * spelled `"/"` on purpose: an empty string never opens the whole FS (it is
 * invalid in both grammars).
 *
 * Canonicalization rejects `..` and backslashes structurally — the SUP-181
 * escape guard, which no rule can override. An empty ref is rejected (a
 * request must name a resource), but the workspace root itself IS a resource —
 * a `listFiles()` of the root — and is spelled `"/"`, matched only by the
 * whole-workspace pattern. The root is the one deliberate exception to the
 * exactly-one-resource coherence law: a directory ref embeds as its subtree
 * pattern.
 *
 * Action lattice: `viewer ⊂ editor ⊂ owner` over primitive verbs
 * read/list/write/delete/share. Requests use primitives; rules may name either.
 */
import { cleanSegments } from "../../fs/paths";
import type { PolicyDomain } from "../types";

const READ = ["read", "list"] as const;
const WRITE = [...READ, "write", "delete"] as const;
const SHARE = [...WRITE, "share"] as const;

/** The canonical whole-workspace subtree pattern. */
export const WORKSPACE_ROOT_PATTERN = "/";

export const fsDomain: PolicyDomain = {
  id: "fs",

  canonicalize(ref) {
    const segs = cleanSegments(ref);
    if (segs === null) return null;
    if (segs.length === 0) {
      // The workspace root — but only when actually addressed ("/", "/."),
      // never as a default reading of the empty string.
      return ref === "" ? null : WORKSPACE_ROOT_PATTERN;
    }
    return segs.join("/");
  },

  canonicalizePattern(pattern) {
    const isSubtree = pattern.endsWith("/");
    const segs = cleanSegments(pattern);
    if (segs === null) return null;
    const joined = segs.join("/");
    // The root is only addressable as the whole-workspace subtree "/" — a
    // bare/empty pattern is invalid rather than silently root-matching.
    if (joined === "") return pattern === "/" ? WORKSPACE_ROOT_PATTERN : null;
    return isSubtree ? `${joined}/` : joined;
  },

  matches(pattern, ref) {
    if (pattern === WORKSPACE_ROOT_PATTERN) return true; // whole workspace
    if (pattern.endsWith("/")) {
      const dir = pattern.slice(0, -1);
      return ref === dir || ref.startsWith(pattern);
    }
    return ref === pattern; // exact
  },

  expandActions(name) {
    switch (name) {
      case "viewer":
        return READ;
      case "editor":
        return WRITE;
      case "owner":
        return SHARE;
      default:
        return null; // a primitive verb — matches itself, no expansion
    }
  },
};
